import React from "react";
import {
  FormControl,
  FormControlProps,
  InputBaseComponentProps,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
} from "@mui/material";

export interface TypedSelectProps<A extends string | number> {
  label: string;
  value: A;
  onChange: (a: A) => void;
  options: A[];
  inputProps?: InputBaseComponentProps;
  formControlProps?: FormControlProps;
}
export function TypedSelect<A extends string | number>(props: TypedSelectProps<A>): React.ReactElement {
  return (
    <FormControl size="small" {...props.formControlProps}>
      <InputLabel>{props.label}</InputLabel>
      <Select
        color="secondary"
        label={props.label}
        value={props.value}
        onChange={(evt) => props.onChange(evt.target.value as A)}
        inputProps={props.inputProps}
        MenuProps={{
          TransitionProps: {
            onExited: () => (document.activeElement as HTMLElement).blur(),
          },
        }}>
        {props.options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export interface TypedOptionalSelectProps<A extends string | number> {
  label: string;
  value: A | undefined;
  onChange: (a: A | undefined) => void;
  options: A[];
  inputProps?: InputBaseComponentProps;
}
export function TypedOptionalSelect<A extends string | number>(props: TypedOptionalSelectProps<A>): React.ReactElement {
  return (
    <FormControl size="small">
      <InputLabel>{props.label}</InputLabel>
      <Select
        color="primary"
        value={props.value}
        onChange={(evt) => props.onChange(evt.target.value as A)}
        inputProps={props.inputProps}
        MenuProps={{
          TransitionProps: {
            onExited: () => (document.activeElement as HTMLElement).blur(),
          },
        }}>
        <MenuItem key="none" value={undefined}>
          None
        </MenuItem>
        {props.options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function PSelect<T = unknown>(props: SelectProps<T>) {
  return (
    <Select<T>
      variant={props.variant || "outlined"}
      margin="dense"
      size="small"
      color="primary"
      MenuProps={{
        TransitionProps: {
          onExited: () => (document.activeElement as HTMLElement).blur(),
        },
        ...props.MenuProps,
      }}
      {...props}
    />
  );
}

export default PSelect;
